import {useFeatureAppEnv} from '@smart/react-utils';
import * as React from 'react';
import type {AppModel} from '../../bff/app-model/create-app-model';
import {fetchAppModel} from '../utils/fetch-app-model';
import type {DelayedStateResult} from './use-delayed-state';
import {useDelayedState} from './use-delayed-state';
import {useLocale} from './use-locale';

export type MaybeAppModel =
  | {readonly appModel: AppModel}
  | {readonly error: unknown};

export function useAppModel(
  done: (error?: Error) => void = () => {},
): [DelayedStateResult<MaybeAppModel>, () => void] {
  const [appModelResult, setAppModel] = useDelayedState<MaybeAppModel>();
  const {baseUrl, featureServices} = useFeatureAppEnv();
  const {'endpoint-directory': endpointDirectory} = featureServices;
  const locale = useLocale();
  const abortControllerRef = React.useRef<AbortController>();

  if (!endpointDirectory) {
    throw Error(`Endpoint directory is undefined.`);
  }

  const fetchAppModelCallback = React.useCallback(() => {
    setAppModel(undefined);
    done();
    abortControllerRef.current?.abort();

    const abortController = (abortControllerRef.current =
      new AbortController());

    fetchAppModel(baseUrl, {
      endpointDirectory,
      locale,
      signal: abortController.signal,
    })
      .then((appModel) => {
        setAppModel({appModel});
        done();
      })
      .catch((error) => {
        if (!abortController.signal.aborted) {
          setAppModel({error});
          done(error);
        }
      });
  }, [baseUrl, endpointDirectory, locale]);

  React.useEffect(fetchAppModelCallback, [fetchAppModelCallback]);
  React.useEffect(() => () => abortControllerRef.current?.abort(), []);

  return [appModelResult, fetchAppModelCallback];
}
